import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgRecipeBulkCreateResponse } from "@/lib/manufacturing/types/recipeImport";

type Summary = MfgRecipeBulkCreateResponse["summary"];

export function ManufacturingModelsBulkImportResults({
  summary,
  confirming,
  confirmError,
  onConfirmAllEligible,
  onPickRecipe,
  onDone,
}: {
  summary: Summary;
  confirming: boolean;
  confirmError: string | null;
  onConfirmAllEligible: () => void;
  onPickRecipe?: (recipeId: string) => void;
  onDone: () => void;
}) {
  const touched = [...summary.created, ...summary.updated];
  const needsReconcile = touched.filter((r) => r.lines_pending > 0);
  const confirmable = touched.filter((r) => r.lines_pending === 0);

  return (
    <div className="space-y-4 text-sm text-gray-800 dark:text-zinc-200">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          Import complete
        </h4>
        <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
          {summary.created.length} created · {summary.updated.length} updated ·{" "}
          {summary.skipped.length} skipped
          {summary.needs_review ? " · all uploads flagged for review" : ""}
        </p>
      </div>

      <section>
        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Successful uploads
        </h5>
        {touched.length === 0 ? (
          <p className="mt-1 text-xs text-gray-500">None.</p>
        ) : (
          <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-gray-100 bg-gray-50 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800/40">
            {touched.map((row) => (
              <li key={row.recipe.id} className="flex items-center justify-between gap-2 py-0.5">
                <span>
                  <code>{row.recipe.code}</code> · {row.recipe.name}
                  <span className="text-gray-500 dark:text-zinc-400">
                    {" "}
                    ({row.lines_matched} matched
                    {row.lines_pending > 0 ? `, ${row.lines_pending} pending` : ""})
                  </span>
                </span>
                {onPickRecipe ? (
                  <button
                    type="button"
                    className={`${ghostBtnClass} px-1.5 py-0.5 text-[11px]`}
                    onClick={() => onPickRecipe(row.recipe.id)}
                  >
                    Open
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h5 className="text-xs font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-300">
          Needs material reconcile
        </h5>
        {needsReconcile.length === 0 ? (
          <p className="mt-1 text-xs text-gray-500">None — all materials matched.</p>
        ) : (
          <ul className="mt-1 max-h-32 overflow-y-auto rounded border border-orange-200 bg-orange-50 px-2 py-1 text-xs text-orange-950 dark:border-orange-800 dark:bg-orange-900/20 dark:text-orange-100">
            {needsReconcile.map((row) => (
              <li key={row.recipe.id} className="flex items-center justify-between gap-2 py-0.5">
                <span>
                  <code>{row.recipe.code}</code> — {row.lines_pending} unmatched material
                  {row.lines_pending === 1 ? "" : "s"}
                </span>
                {onPickRecipe ? (
                  <button
                    type="button"
                    className={`${ghostBtnClass} px-1.5 py-0.5 text-[11px]`}
                    onClick={() => onPickRecipe(row.recipe.id)}
                  >
                    Reconcile
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h5 className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
          Needs confirmation
        </h5>
        <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
          Confirm clears the review flag. Recipes with pending materials cannot be confirmed yet
          ({confirmable.length} of {touched.length} eligible).
        </p>
        {confirmError ? (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400">{confirmError}</div>
        ) : null}
      </section>

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={mfgSecondaryBtnClass} onClick={onDone}>
          Close
        </button>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={confirming || confirmable.length === 0}
          onClick={onConfirmAllEligible}
        >
          {confirming
            ? "Confirming…"
            : `Confirm ${confirmable.length} eligible`}
        </button>
      </div>
    </div>
  );
}
